import { keyBy, merge } from 'lodash';
import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { ResourceManagmentApi } from 'api/resource-management';
import {
  ResourceType,
  AvailabilityForTeamWithProject,
  DeleteAllocationsProps,
  AllocationsGroupByUsersOrTeamsLookup,
  AllocationsGroupByUsersOrTeams,
  AllocationsGroupByUser,
  AllocationsGroupByTeamLookup,
  AllocationsGroupByTeam,
  AllocationsGroupByUserLookup,
  ConvertAllocationsPropsApi,
} from 'api/resource-management/types';

// * Moving this function to id.ts and importing from there throws some error in ckEditor
export const aggregationIdForUser = (
  projectId: number,
  roleId?: number,
  referenceTeamId?: number,
): string =>
  `project-${projectId}${roleId ? `_role-${roleId}` : ''}${
    referenceTeamId ? `_referenceTeam-${referenceTeamId}` : ''
  }`;

const allocationByUserToLookup = ({
  aggregations,
  ...rest
}: AllocationsGroupByUser): AllocationsGroupByUserLookup => ({
  ...rest,
  aggregationLookup: keyBy(
    aggregations,
    ({ projectId, roleId, referenceTeamId }) =>
      aggregationIdForUser(projectId, roleId, referenceTeamId),
  ),
});

const allocationByTeamToLookup = ({
  aggregations,
  ...rest
}: AllocationsGroupByTeam): AllocationsGroupByTeamLookup => ({
  ...rest,
  aggregationLookup: keyBy(
    aggregations,
    ({ projectId }) => `project-${projectId}`,
  ),
});

export const allocationGroupedByPeopleToLookup = (
  data: AllocationsGroupByUsersOrTeams,
): AllocationsGroupByUsersOrTeamsLookup =>
  data.resources.reduce((result, resource) => {
    /**
     * There will be multiple records of same user each corresponding to an instance in RM
     * Hence merging the result
     * */
    if (resource.type === ResourceType.User) {
      return merge(result, {
        [`user-${resource.user.userId}`]: allocationByUserToLookup(resource),
      });
    }

    return merge(result, {
      [`team-${resource.team.teamId}`]: allocationByTeamToLookup(resource),
    });
  }, {});

export const getAllocationAggregationKey = (): QueryKey => [
  'allocations',
  'aggregations',
];

export const getResourceAllocationsForPeople = (): QueryKey => [
  'allocations',
  'people',
];

export type ResourceAvailabilityForTeamQueryKey = [string, number, number];

export const getResourceAvailabilityForTeamQueryKey = (
  projectId: number,
  teamId: number,
): ResourceAvailabilityForTeamQueryKey => [
  'resource-availability',
  projectId,
  teamId,
];

export const useResourceAvailabilityForTeam = (
  projectId: number,
  teamId: number,
  queryOptions: UseQueryOptions<
    AvailabilityForTeamWithProject,
    unknown,
    AvailabilityForTeamWithProject,
    ResourceAvailabilityForTeamQueryKey
  > = {},
): UseQueryResult<AvailabilityForTeamWithProject> =>
  useQuery(
    getResourceAvailabilityForTeamQueryKey(projectId, teamId),
    () => ResourceManagmentApi.getResourceAvailaibility(projectId, teamId),
    {
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      ...queryOptions,
    },
  );

export const useDeleteResourceAllocationsQuery = (
  options: UseMutationOptions<void, unknown, DeleteAllocationsProps> = {},
): UseMutationResult<void, unknown, DeleteAllocationsProps> =>
  useMutation(
    (params) => ResourceManagmentApi.deleteResourceAllocations(params),
    options,
  );

export const useConvertResourceAllocationsQuery = (
  options: UseMutationOptions<void, unknown, ConvertAllocationsPropsApi> = {},
): UseMutationResult<void, unknown, ConvertAllocationsPropsApi> =>
  useMutation(
    (params) => ResourceManagmentApi.convertResourceAllocations(params),
    options,
  );
